import { Button, Tooltip } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectPageModal } from 'src/features/modals/select-page-modal';
import { UploadModal } from 'src/features/modals/upload-modal';
import DownloadIcon from 'src/shared/assets/icons/download-arrow.svg';
import { PlanVersionType } from 'src/store/comparison/types';
import {
  GOPLAN_DRILLING_CARPET_IMPORT_ADMIN,
  GOPLAN_DRILLING_CARPET_IMPORT_EDITOR,
  GOPLAN_DRILLING_CARPET_CREATE_WELL,
  GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLIC_DRAFT_EDIT,
} from 'src/store/roles-store/roles';
import { useStore } from 'src/store/use-store';

import styles from './compare-and-import-export.module.scss';
import { UploadButtonStore } from './upload-button.store';

export const UploadButton = observer(function UploadButton() {
  const { t } = useTranslation('drillingCarpet');

  const store = useStore();
  const { comparison, editing, jobsStore, planVersion, planVersionsService, rolesStore } = store;

  const [uploadButtonStore] = useState(() => new UploadButtonStore(store));
  const {
    openUploadModal,
    isSelectPageModalOpened,
    isUploadModalOpened,
    closeUploadModal,
    closeSelectPageModal,
    uploadFile,
    isLoading,
    sheets,
    selectSheet,
  } = uploadButtonStore;

  const isImportAvailable = useMemo(() => {
    if (!planVersionsService.currentPlanVersion) {
      return false;
    }

    if (planVersionsService.currentPlanVersion.data.versionType === PlanVersionType.public) {
      return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_IMPORT_ADMIN]);
    }

    if (planVersionsService.currentPlanVersion.data.versionType === PlanVersionType.personal) {
      return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_IMPORT_ADMIN, GOPLAN_DRILLING_CARPET_IMPORT_EDITOR]);
    }

    return false;
  }, [planVersionsService.currentPlanVersion, rolesStore]);

  const isCreateWellAvailable = useMemo(() => {
    if (!rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_CREATE_WELL])) {
      return false;
    }

    const currentVersion = planVersionsService.currentPlanVersion;

    if (!currentVersion) {
      return false;
    }

    // Создание скважины доступно только в редактируемых версиях (зеркально с isImportAvailable):
    //   - публичный черновик — при праве редактирования публичного черновика;
    //   - личный черновик — всегда (черновик пользователя).
    // На "Опубликованной" версии напрямую создавать скважину нельзя — нужно сначала
    // перейти в черновик через EditPublicDraftModal.
    // На "Архивных" версиях редактирование сейчас доступно как временное решение
    // пользователям с GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT (или GOD-ролью FULL_ACCESS_ROLE),
    // но создание скважины из этой кнопки не предусмотрено — симметрично с isImportAvailable.
    if (currentVersion.data.versionType === PlanVersionType.public) {
      return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLIC_DRAFT_EDIT]);
    }

    if (currentVersion.data.versionType === PlanVersionType.personal) {
      return true;
    }

    return false;
  }, [planVersionsService.currentPlanVersion, rolesStore]);

  if (!isImportAvailable && !isCreateWellAvailable) {
    return null;
  }

  return (
    <>
      <Tooltip title={t('common:import')}>
        <Button
          variant="flat"
          onClick={openUploadModal}
          icon={<DownloadIcon className={styles.icon} />}
          disabled={
            editing.isEditing ||
            comparison.isComparing ||
            !!jobsStore.currentImportVersionJob ||
            planVersion.version?.data.isArchive
          }
          className={clsx(styles.iconButton, comparison.isComparing && styles.hidden)}
        />
      </Tooltip>

      <UploadModal
        isUploadModalOpened={isUploadModalOpened}
        isLoading={isLoading}
        isImportAvailable={isImportAvailable}
        isCreateWellAvailable={isCreateWellAvailable}
        onClose={closeUploadModal}
        onUpload={uploadFile}
      />
      <SelectPageModal
        sheets={sheets ?? []}
        onSelectSheet={selectSheet}
        onClose={closeSelectPageModal}
        isOpen={isSelectPageModalOpened}
      />
    </>
  );
});
